import { BUSINESS_TYPES } from '../CONSTANTS';
import { normalizeBusinessType } from './businessType';

export type BusinessType = (typeof BUSINESS_TYPES)[number];

/**
 * Default category list when business type is missing or unknown (matches "General Shop").
 * Plain labels only — no parentheses; suitable for `bp_product_category` payloads.
 */
export const DEFAULT_PRELOAD_CATEGORIES: readonly string[] = [
  'Groceries',
  'Beverages',
  'Household',
  'Personal care',
  'Snacks & candy',
  'Basic hardware',
  'Misc retail',
];

export const PRELOAD_CATEGORIES_BY_BUSINESS_TYPE: Record<
  BusinessType,
  readonly string[]
> = {
  Agrovet: [
    'Animal feed',
    'Veterinary medicine',
    'Vaccines & dewormers',
    'Poultry supplies',
    'Cattle & dairy supplies',
    'Pet care',
    'Farm tools & equipment',
  ],
  AutoSpares: [
    'Engine parts',
    'Filters & fluids',
    'Brakes & suspension',
    'Electrical & batteries',
    'Body & lights',
    'Tyres & wheels',
    'Car care & accessories',
  ],
  'Bakery & Pastry': [
    'Breads & rolls',
    'Cakes & pastries',
    'Cookies & biscuits',
    'Savory bakes',
    'Ingredients & supplies',
    'Beverages',
    'Packaging',
  ],
  'Bar & Restaurant': [
    'Starters & sides',
    'Mains',
    'Desserts',
    'Non-alcoholic drinks',
    'Beer & cider',
    'Wine & spirits',
    'Bar consumables & mixers',
  ],
  'Beauty & Cosmetics': [
    'Hair care',
    'Skin care',
    'Makeup',
    'Nails',
    'Fragrances',
    'Tools & brushes',
    'Salon consumables',
  ],
  BookShop: [
    'Fiction',
    'Non-fiction',
    "Children's books",
    'Educational & textbooks',
    'Stationery',
    'Magazines & cards',
    'Gifts & accessories',
  ],
  'Boutique & Apparel': [
    'Tops & knitwear',
    'Bottoms & dresses',
    'Outerwear',
    'Footwear',
    'Bags & wallets',
    'Jewelry & accessories',
    'Beauty & gifts',
  ],
  Butchery: [
    'Beef',
    'Poultry',
    'Pork & lamb',
    'Fish & seafood',
    'Offal & specialty cuts',
    'Marinated & ready-to-cook',
    'Cold cuts & deli',
  ],
  'Coffee Shop': [
    'Coffee beans & grounds',
    'Hot drinks',
    'Cold drinks',
    'Pastries & snacks',
    'Sandwiches & meals',
    'Syrups & toppings',
    'Merch & retail',
  ],
  'General Electronics': [
    'Phones & tablets',
    'Computers & laptops',
    'TV & audio',
    'Accessories & cables',
    'Power & charging',
    'Smart home',
    'Spare parts & components',
  ],
  'Cereals & Grains': [
    'White maize grain',
    'Yellow maize grain',
    'Sifted maize flour',
    'Whole maize meal',
    'Fortified blended meal',
    'Beans & pulses',
    'Rice',
    'Lentils & green grams',
  ],
  'Phone Accessories & Repairs': [
    'Phone cases & covers',
    'Screen protectors',
    'Charging cables & adapters',
    'Power banks',
    'Earphones & headsets',
    'Replacement batteries',
    'Screens & digitizers',
    'Phone spare parts',
  ],
  'Fruits & Vegetables Vendor': [
    'Leafy greens',
    'Tomatoes & onions',
    'Fruits',
    'Root vegetables',
    'Herbs & spices',
    'Legumes',
    'Packaging & bags',
  ],
  'Milk ATM': [
    'Fresh milk',
    'Fermented milk',
    'Yoghurt',
    'Flavoured milk',
    'Cream & butter',
    'Eggs',
    'Cups & packaging',
  ],
  'Stationery & Printing': [
    'Paper & notebooks',
    'Pens & pencils',
    'Files & folders',
    'Office supplies',
    'Printer ink & toner',
    'Art & craft supplies',
    'School supplies',
  ],
  'Furniture and Fittings': [
    'Sofas & chairs',
    'Beds & mattresses',
    'Tables & dining',
    'Office furniture',
    'Storage & wardrobes',
    'Door & cabinet fittings',
    'Handles & hinges',
  ],
  'Home & Kitchen': [
    'Cookware',
    'Bakeware',
    'Tableware',
    'Kitchen tools',
    'Small appliances',
    'Food storage',
    'Cleaning supplies',
  ],
  'Mini Mart': [
    'Dry goods & staples',
    'Snacks',
    'Beverages',
    'Household & cleaning',
    'Personal care',
    'Dairy & eggs',
    'Bread & bakery',
    'Frozen',
  ],
  'General Shop': [...DEFAULT_PRELOAD_CATEGORIES],
  'Gift Shop': [
    'Souvenirs',
    'Home décor',
    'Candles & fragrance',
    'Toys & games',
    'Seasonal gifts',
    'Wrapping & bags',
    'Confectionery',
  ],
  Grocery: [
    'Fresh produce',
    'Dairy & eggs',
    'Meat & fish',
    'Bakery',
    'Dry goods & staples',
    'Frozen',
    'Snacks & beverages',
  ],
  'Hardware Store': [
    'Hand & power tools',
    'Paint & sundries',
    'Plumbing',
    'Electrical',
    'Building materials',
    'Fasteners & hardware',
    'Garden & outdoor',
  ],
  Hotel: [
    'F&B retail & minibar',
    'Toiletries & amenities',
    'Linen & housekeeping',
    'Mini-bar restock',
    'Spa & wellness retail',
    'Souvenirs',
    'Maintenance supplies',
  ],
  'Jewelry Store': [
    'Rings',
    'Necklaces & pendants',
    'Earrings',
    'Bracelets & bangles',
    'Watches',
    'Gifts & packaging',
    'Care & accessories',
  ],
  Kiosk: [
    'Packaged drinks',
    'Snacks & candy',
    'Basic groceries',
    'Phone airtime & cards',
    'Tobacco & lighters',
    'Personal care basics',
    'Misc impulse',
  ],
  Pharmacy: [
    'OTC medicines',
    'Vitamins & supplements',
    'First aid',
    'Personal care',
    'Baby care',
    'Medical devices',
    'Beauty & skincare',
  ],
  Store: [
    'Groceries',
    'Beverages',
    'Household',
    'Personal care',
    'Snacks',
    'Electronics basics',
    'General merchandise',
  ],
  Supermarket: [
    'Produce',
    'Meat & seafood',
    'Dairy & chilled',
    'Bakery',
    'Grocery & dry goods',
    'Frozen',
    'Health & beauty',
    'Household & cleaning',
  ],
  'Wines and Spirits': [
    'Wine',
    'Spirits',
    'Beer & cider',
    'Mixers & soft drinks',
    'Ice & water',
    'Glassware & bar tools',
    'Tobacco alternatives',
    'Gift packs',
  ],
};

/**
 * Returns a fresh array of preload category labels for the POS category modal.
 * Unknown or empty business type → General Shop list (`DEFAULT_PRELOAD_CATEGORIES`).
 * Resolves legacy `bp_business_type` strings via `normalizeBusinessType`.
 */
export function getPreloadCategoriesForBusinessType(
  businessType: string | undefined | null,
): string[] {
  const canonical = normalizeBusinessType(businessType);
  const key = canonical as BusinessType;
  const list = PRELOAD_CATEGORIES_BY_BUSINESS_TYPE[key];
  if (list?.length) {
    return [...list];
  }
  return [...DEFAULT_PRELOAD_CATEGORIES];
}
